import * as Sentry from '@sentry/react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: 'var(--background-primary)',
        color: 'var(--foreground-primary)',
        textAlign: 'center',
      }}
    >
      <AlertTriangle
        size={48}
        style={{ color: 'var(--error)', marginBottom: '1rem' }}
      />
      <h1
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
        }}
      >
        Something went wrong
      </h1>
      <p
        style={{
          color: 'var(--foreground-secondary)',
          marginBottom: '1.5rem',
          maxWidth: '400px',
        }}
      >
        We've been notified and are working on a fix. Please try again.
      </p>
      {import.meta.env.DEV && (
        <pre
          style={{
            backgroundColor: 'var(--background-secondary)',
            padding: '1rem',
            borderRadius: '8px',
            fontSize: '0.75rem',
            maxWidth: '100%',
            overflow: 'auto',
            marginBottom: '1.5rem',
            textAlign: 'left',
          }}
        >
          {error.message}
        </pre>
      )}
      <button
        onClick={resetError}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: 'var(--primary)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <RotateCcw size={16} />
        Try again
      </button>
    </div>
  );
}

export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <ErrorFallback error={error as Error} resetError={resetError} />
    ),
    showDialog: false, // Set to true to show Sentry feedback dialog
  }
);
